<template>
  <div class="customer-details-main">
    <!-- Header with customer info -->
    <VCard class="mb-6 customer-details-header">
      <VCardText class="pa-4">
        <div class="d-flex align-center justify-space-between">
          <div class="d-flex align-center">
            <VBtn
              icon
              variant="text"
              color="primary"
              class="me-3 back-button"
              @click="goBack"
            >
              <VIcon icon="tabler-arrow-left" />
            </VBtn>
              
            <VAvatar
              color="primary"
              variant="tonal"
              size="48"
              class="me-3 customer-avatar"
            >
              <VIcon
                icon="tabler-user"
                size="24"
              />
            </VAvatar>
              
            <div class="customer-header-info">
              <h2 class="text-h5 mb-1 customer-name">
                {{ customer.name || 'Loading...' }}
              </h2>
              <div class="d-flex align-center gap-2">
                <VChip
                  color="primary"
                  size="small"
                  class="customer-id-chip"
                >
                  ID: {{ customer.id }}
                </VChip>
                <VChip
                  color="success"
                  size="small"
                  prepend-icon="tabler-circle-check"
                  class="status-chip"
                >
                  {{ $t('history.customers.active_customer') }}
                </VChip>
              </div>
            </div>
          </div>
            
          <div class="d-flex gap-2 customer-header-actions">
            <VBtn
              color="secondary"
              variant="outlined"
              prepend-icon="tabler-file-analytics"
              class="generate-report-button"
              @click="generateCustomerReport"
            >
              {{ $t('history.customers.customer_report') }}
            </VBtn>

            <VBtn
              color="primary"
              variant="outlined"
              prepend-icon="tabler-download"
              class="export-data-button"
              @click="exportCustomerData"
            >
              {{ $t('history.customers.export_customer_data') }}
            </VBtn>

            <!-- Merge into another customer (manual de-dup). The operator
                 picks a target contact and we POST to
                 /shipment/customers/{id}/merge-into/{targetId}. Backend
                 reassigns parcels + client groups + invoices and marks
                 the source as IDENTITY_MERGED. -->
            <VBtn
              v-if="customer.identity_status !== 'merged'"
              color="warning"
              variant="outlined"
              prepend-icon="tabler-arrow-merge"
              class="merge-button"
              @click="openMergeDialog"
            >
              {{ $t('history.customers.merge_into') || 'Fusionner' }}
            </VBtn>

            <!-- Delete-if-empty: cleanup orphan placeholders that have
                 no parcels, groups, invoices, chat identities, and
                 nothing merged into them. Gated on the backend's
                 `is_empty` flag (computed in HistoryController::
                 customerDetails). -->
            <VBtn
              v-if="customer.is_empty"
              color="error"
              variant="outlined"
              prepend-icon="tabler-trash"
              class="delete-button"
              @click="deleteDialog = true"
            >
              {{ $t('history.customers.delete_empty') || 'Supprimer' }}
            </VBtn>
          </div>
        </div>
      </VCardText>
    </VCard>
  
    <VRow>
      <!-- Customer Information -->
      <VCol
        cols="12"
        lg="8"
      >
        <VCard class="mb-4 customer-info-card">
          <VCardTitle class="d-flex align-center pa-4">
            <VIcon
              icon="tabler-info-circle"
              class="me-3"
            />
            {{ $t('history.customers.customer_information') }}
          </VCardTitle>
            
          <VCardText class="pa-4">
            <VRow>
              <VCol
                cols="12"
                md="6"
              >
                <VList
                  density="compact"
                  class="info-list"
                >
                  <VListItem class="px-0">
                    <template #prepend>
                      <VIcon
                        color="primary"
                        size="20"
                      >
                        tabler-user
                      </VIcon>
                    </template>
                    <VListItemTitle class="text-sm">
                      {{ $t('shipment.customer_name') }}
                    </VListItemTitle>
                    <VListItemSubtitle>{{ customer.name || '-' }}</VListItemSubtitle>
                  </VListItem>
                    
                  <VListItem class="px-0">
                    <template #prepend>
                      <VIcon
                        color="primary"
                        size="20"
                      >
                        tabler-phone
                      </VIcon>
                    </template>
                    <VListItemTitle class="text-sm">
                      {{ $t('shipment.phone') }}
                    </VListItemTitle>
                    <VListItemSubtitle>{{ customer.phone || '-' }}</VListItemSubtitle>
                  </VListItem>
                    
                  <VListItem class="px-0">
                    <template #prepend>
                      <VIcon
                        color="primary"
                        size="20"
                      >
                        tabler-mail
                      </VIcon>
                    </template>
                    <VListItemTitle class="text-sm">
                      {{ $t('shipment.email') }}
                    </VListItemTitle>
                    <VListItemSubtitle>{{ customer.email || '-' }}</VListItemSubtitle>
                  </VListItem>
                </VList>
              </VCol>
                
              <VCol
                cols="12"
                md="7"
              >
                <VList
                  density="compact"
                  class="info-list"
                >
                  <VListItem class="px-0">
                    <template #prepend>
                      <VIcon
                        color="success"
                        size="20"
                      >
                        tabler-calendar-plus
                      </VIcon>
                    </template>
                    <VListItemTitle class="text-sm">
                      {{ $t('history.customers.customer_since') }}
                    </VListItemTitle>
                    <VListItemSubtitle>{{ formatDate(customer.created_at) }}</VListItemSubtitle>
                  </VListItem>
                    
                  <VListItem class="px-0">
                    <template #prepend>
                      <VIcon
                        color="info"
                        size="20"
                      >
                        tabler-calendar
                      </VIcon>
                    </template>
                    <VListItemTitle class="text-sm">
                      {{ $t('history.customers.last_order') }}
                    </VListItemTitle>
                    <VListItemSubtitle>{{ formatDate(customer.last_order_date) }}</VListItemSubtitle>
                  </VListItem>
                    
                  <VListItem class="px-0">
                    <template #prepend>
                      <VIcon
                        color="warning"
                        size="20"
                      >
                        tabler-star
                      </VIcon>
                    </template>
                    <VListItemTitle class="text-sm">
                      {{ $t('history.customers.satisfaction') }}
                    </VListItemTitle>
                    <VListItemSubtitle>
                      <VRating
                        :model-value="customer.satisfaction || 5"
                        size="small"
                        color="warning"
                        readonly
                        density="compact"
                      />
                    </VListItemSubtitle>
                  </VListItem>
                </VList>
              </VCol>
            </VRow>
          </VCardText>
        </VCard>

        <!-- Verified chat identities (Messenger / WhatsApp / web).
             Only renders when there's at least one — keeps the page
             clean for customers who never used /iam. -->
        <ChatIdentitiesPanel
          v-if="customer && customer.chat_identities && customer.chat_identities.length"
          :identities="customer.chat_identities"
          :contact-name="customer.name"
          variant="panel"
          class="mt-4"
        />

        <!-- Order History -->
        <VCard class="order-history-card">
          <VCardTitle class="d-flex align-center justify-space-between pa-4">
            <div class="d-flex align-center">
              <VIcon
                icon="tabler-history"
                class="me-3"
              />
              {{ $t('history.customers.order_history') }}
            </div>
            <VChip
              color="primary"
              size="small"
            >
              {{ orders.length }}
            </VChip>
          </VCardTitle>
            
          <VCardText class="pa-4">
            <div
              v-if="loadingOrders"
              class="text-center py-4"
            >
              <VProgressCircular
                indeterminate
                color="primary"
                size="32"
              />
            </div>
              
            <div
              v-else-if="orders.length === 0"
              class="text-center py-4 no-orders-section"
            >
              <VIcon
                icon="tabler-package-off"
                size="48"
                class="mb-2 text-disabled"
              />
              <p class="text-subtitle-2 mb-0">
                {{ $t('history.customers.no_orders_found') }}
              </p>
            </div>
              
            <div v-else>
              <VDataTable
                :items="orders"
                :headers="orderHeaders"
                :loading="loadingOrders"
                class="order-history-table"
                :items-per-page="10"
                hide-default-footer
              >
                <template #item.barcode="{ item }">
                  <VChip
                    color="primary"
                    size="small"
                    class="order-barcode"
                    :to="{ name: 'shipment-incoming-parcels-id', params: { id: item.id } }"
                    link
                  >
                    {{ item.barcode }}
                  </VChip>
                </template>
                  
                <template #item.status="{ item }">
                  <VChip
                    :color="getStatusColor(item.status)"
                    size="small"
                    class="order-status"
                  >
                    {{ $t(`shipment.statuses.${item.status}`, item.status) }}
                  </VChip>
                </template>
                  
                <template #item.weight="{ item }">
                  <div class="d-flex align-center">
                    <VIcon icon="tabler-weight" size="16" class="me-1" />
                    <span class="font-weight-bold text-success">
                      {{ formatWeight(item.weight || 0) }}
                    </span>
                  </div>
                </template>
                  
                <template #item.volume="{ item }">
                  <div class="d-flex align-center">
                    <VIcon icon="tabler-box" size="16" class="me-1" />
                    <span class="font-weight-bold text-info">
                      {{ formatVolume(item.volume || 0) }}
                    </span>
                  </div>
                </template>
                  
                <template #item.created_at="{ item }">
                  {{ formatDate(item.created_at) }}
                </template>
                  
                <template #item.actions="{ item }">
                  <VBtn
                    icon
                    variant="text"
                    size="small"
                    color="primary"
                    @click="viewOrderDetails(item)"
                  >
                    <VIcon icon="tabler-eye" />
                  </VBtn>
                </template>
              </VDataTable>
            </div>
          </VCardText>
        </VCard>
      </VCol>
  
      <!-- Statistics Sidebar -->
      <VCol
        cols="12"
        lg="3"
      >
        <VCard class="mb-4 statistics-card">
          <VCardTitle class="d-flex align-center pa-4">
            <VIcon
              icon="tabler-chart-bar"
              class="me-3"
            />
            {{ $t('history.customers.statistics') }}
          </VCardTitle>
            
          <VCardText class="pa-4">
            <div class="statistics-grid">
              <div class="stat-item text-center mb-3">
                <VAvatar size="40" color="primary" variant="tonal" class="mb-2">
                  <VIcon icon="tabler-package" size="20" />
                </VAvatar>
                <h4 class="text-h6 font-weight-bold mb-1 text-primary">
                  {{ customer.total_orders || 0 }}
                </h4>
                <p class="text-caption mb-0">
                  {{ $t('history.customers.total_orders') }}
                </p>
              </div>
                
              <div class="stat-item text-center mb-3">
                <VAvatar size="40" color="success" variant="tonal" class="mb-2">
                  <VIcon icon="tabler-weight" size="20" />
                </VAvatar>
                <h4 class="text-h6 font-weight-bold mb-1 text-success">
                  {{ formatWeight(customer.total_weight || 0) }}
                </h4>
                <p class="text-caption mb-0">
                  {{ $t('history.customers.total_weight') }}
                </p>
              </div>
                
              <div class="stat-item text-center mb-3">
                <VAvatar size="40" color="info" variant="tonal" class="mb-2">
                  <VIcon icon="tabler-box" size="20" />
                </VAvatar>
                <h4 class="text-h6 font-weight-bold mb-1 text-info">
                  {{ formatVolume(customer.total_volume || 0) }}
                </h4>
                <p class="text-caption mb-0">
                  {{ $t('history.customers.total_volume') }}
                </p>
              </div>
                
              <div class="stat-item text-center">
                <VAvatar size="40" color="warning" variant="tonal" class="mb-2">
                  <VIcon icon="tabler-star" size="20" />
                </VAvatar>
                <h4 class="text-h6 font-weight-bold mb-1 text-warning">
                  {{ customer.satisfaction || 5 }}/5
                </h4>
                <p class="text-caption mb-0">
                  {{ $t('history.customers.satisfaction_rating') }}
                </p>
              </div>
            </div>
          </VCardText>
        </VCard>
      </VCol>
    </VRow>

    <!-- Manual merge dialog. Used to fold a placeholder/duplicate
         contact (this page) INTO an existing canonical contact. After
         a successful merge the source becomes IDENTITY_MERGED and is
         hidden from the customer list; the target absorbs parcels,
         groups and invoices. -->
    <VDialog v-model="mergeDialog" max-width="640" persistent>
      <VCard>
        <VCardTitle class="d-flex align-center gap-2">
          <VIcon icon="tabler-arrow-merge" color="warning" />
          {{ $t('history.customers.merge_dialog_title') || 'Fusionner ce client dans un autre' }}
        </VCardTitle>

        <VCardText>
          <p class="text-body-2 mb-3">
            {{ $t('history.customers.merge_dialog_body') ||
              'Choisissez le client cible. Tous les colis, groupes et factures de ce client seront transférés vers la cible.' }}
          </p>

          <!-- Source recap -->
          <VAlert
            type="info"
            variant="tonal"
            density="compact"
            class="mb-3"
          >
            <div class="font-weight-medium">{{ customer.name }}</div>
            <div class="text-caption">
              ID #{{ customer.id }} · {{ customer.phone || '—' }}
            </div>
          </VAlert>

          <!-- Target picker -->
          <VAutocomplete
            v-model="mergeTarget"
            v-model:search="targetSearch"
            :items="targetCandidates"
            :loading="targetLoading"
            item-title="display_name"
            item-value="id"
            :label="$t('history.customers.merge_target') || 'Client cible'"
            :placeholder="$t('shipment.search_customer') || 'Rechercher un client…'"
            prepend-inner-icon="tabler-user-search"
            density="comfortable"
            hide-no-data
            clearable
            return-object
            no-filter
            @update:model-value="val => mergeTarget = val"
          >
            <template #item="{ props: itemProps, item }">
              <VListItem v-bind="itemProps" :title="item.raw.name">
                <VListItemSubtitle>
                  {{ item.raw.phone || '—' }}
                  <template v-if="item.raw.client_id">· {{ item.raw.client_id }}</template>
                  <template v-if="item.raw.identity_status === 'verified'">
                    · <span class="text-success">{{ $t('history.customers.verified') || 'Vérifié' }}</span>
                  </template>
                </VListItemSubtitle>
              </VListItem>
            </template>
          </VAutocomplete>

          <VAlert
            v-if="mergeTarget && mergeTarget.id"
            type="warning"
            variant="tonal"
            density="compact"
            class="mt-3"
          >
            <div class="font-weight-medium">{{ $t('history.customers.merge_confirm_heading') || 'Confirmation' }}</div>
            <div class="text-caption">
              {{ $t('history.customers.merge_confirm_body', {
                source: customer.name,
                target: mergeTarget.name,
              }) || `« ${customer.name} » sera marqué comme fusionné dans « ${mergeTarget.name} ». Cette action ne peut pas être annulée.` }}
            </div>
          </VAlert>
        </VCardText>

        <VCardActions>
          <VSpacer />
          <VBtn variant="text" :disabled="mergeBusy" @click="mergeDialog = false">
            {{ $t('common.cancel') }}
          </VBtn>
          <VBtn
            color="warning"
            :loading="mergeBusy"
            :disabled="!mergeTarget || !mergeTarget.id"
            @click="submitMerge"
          >
            {{ $t('history.customers.merge_action') || 'Fusionner' }}
          </VBtn>
        </VCardActions>
      </VCard>
    </VDialog>

    <!-- Delete-empty confirmation. Only reachable when customer.is_empty
         is true (backend-gated). Frontend confirms intent then calls
         DELETE /shipment/customers/{id}/delete-if-empty. -->
    <VDialog v-model="deleteDialog" max-width="520" persistent>
      <VCard>
        <VCardTitle class="d-flex align-center gap-2">
          <VIcon icon="tabler-trash" color="error" />
          {{ $t('history.customers.delete_dialog_title') || 'Supprimer ce client vide' }}
        </VCardTitle>
        <VCardText>
          <p class="text-body-2 mb-3">
            {{ $t('history.customers.delete_dialog_body') ||
              'Ce client n\'a aucun colis, groupe, facture ou identité chat. Voulez-vous le supprimer définitivement ?' }}
          </p>
          <VAlert type="info" variant="tonal" density="compact">
            <div class="font-weight-medium">{{ customer.name }}</div>
            <div class="text-caption">
              ID #{{ customer.id }} · {{ customer.phone || '—' }}
            </div>
          </VAlert>
        </VCardText>
        <VCardActions>
          <VSpacer />
          <VBtn variant="text" :disabled="deleteBusy" @click="deleteDialog = false">
            {{ $t('common.cancel') }}
          </VBtn>
          <VBtn color="error" :loading="deleteBusy" @click="submitDelete">
            {{ $t('history.customers.delete_action') || 'Supprimer' }}
          </VBtn>
        </VCardActions>
      </VCard>
    </VDialog>
  </div>
</template>
  
<script setup>
import { ref, onMounted, computed, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useSnackbar } from '@/composables/useSnackbar'
import { useRoute, useRouter } from 'vue-router'
import axios from 'axios'
import ChatIdentitiesPanel from '@/components/customer/ChatIdentitiesPanel.vue'
import { getStatusColor } from '@/utils/statusUtils'
  
definePage({
  meta: {
    requiresAuth: true,
    role: ['admin', 'manager', 'user'],
  },
})
  
const { t } = useI18n()
const { showSnackbar } = useSnackbar()
const route = useRoute()
const router = useRouter()
  
// Props
const customerId = computed(() => route.params.customerId)
  
// Reactive data
const customer = ref({})
const orders = ref([])
const loadingCustomer = ref(false)
const loadingOrders = ref(false)
  
// Table headers
const orderHeaders = computed(() => [
  { title: t('shipment.barcode'), key: 'barcode', sortable: true },
  { title: t('shipment.status'), key: 'status', sortable: true },
  { title: t('shipment.container'), key: 'container_number', sortable: true },
  { title: t('history.customers.weight'), key: 'weight', sortable: true },
  { title: t('history.customers.volume'), key: 'volume', sortable: true },
  { title: t('shipment.created_at'), key: 'created_at', sortable: true },
  { title: t('common.actions'), key: 'actions', sortable: false },
])
  
// Methods
const fetchCustomer = async () => {
  try {
    loadingCustomer.value = true
      
    const response = await axios.get(`/shipment/history/customers/${customerId.value}`)
    customer.value = response.data.data
  } catch (error) {
    console.error('Error fetching customer:', error)
    showSnackbar({
      color: 'error',
      text: t('messages.fetch_failed', { item: 'customer' }),
    })
  } finally {
    loadingCustomer.value = false
  }
}
  
const fetchOrders = async () => {
  try {
    loadingOrders.value = true
      
    const response = await axios.get(`/shipment/history/customers/${customerId.value}/orders`)
    orders.value = response.data.data
  } catch (error) {
    console.error('Error fetching orders:', error)
    showSnackbar({
      color: 'error',
      text: t('messages.fetch_failed', { item: 'orders' }),
    })
  } finally {
    loadingOrders.value = false
  }
}
  
const formatDate = date => {
  if (!date) return '-'
  try {
    return new Date(date).toLocaleDateString()
  } catch (e) {
    return '-'
  }
}

const formatWeight = weight => {
  const numWeight = parseFloat(weight) || 0
  return `${numWeight.toFixed(1)} kg`
}

const formatVolume = volume => {
  const numVolume = parseFloat(volume) || 0
  return `${numVolume.toFixed(2)} m³`
}
  
const goBack = () => {
  router.push({ name: 'history-customers' })
}
  
const generateCustomerReport = async () => {
  try {
    const response = await axios.get(`/shipment/history/customers/${customerId.value}/report`, {
      responseType: 'blob',
    })
      
    const blob = new Blob([response.data], { type: 'application/pdf' })
    const url = window.URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.setAttribute('download', `customer-${customerId.value}-report.pdf`)
    document.body.appendChild(link)
    link.click()
    window.URL.revokeObjectURL(url)
    document.body.removeChild(link)
      
    showSnackbar({
      color: 'success',
      text: t('messages.report_generated'),
    })
  } catch (error) {
    console.error('Error generating report:', error)
    showSnackbar({
      color: 'error',
      text: t('messages.report_generation_failed'),
    })
  }
}
  
const exportCustomerData = async () => {
  try {
    const response = await axios.get(`/shipment/history/customers/${customerId.value}/export`, {
      responseType: 'blob',
    })
      
    const blob = new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
    const url = window.URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.setAttribute('download', `customer-${customerId.value}-data.xlsx`)
    document.body.appendChild(link)
    link.click()
    window.URL.revokeObjectURL(url)
    document.body.removeChild(link)
      
    showSnackbar({
      color: 'success',
      text: t('messages.export_successful'),
    })
  } catch (error) {
    console.error('Error exporting data:', error)
    showSnackbar({
      color: 'error',
      text: t('messages.export_failed'),
    })
  }
}
  
const viewOrderDetails = order => {
  // Navigate to order details or show modal
  console.log('View order details:', order)
}
  
// ───────── Delete empty customer ─────────
// Cleanup for orphan placeholder contacts (smart-create rows that
// never got attached, post-merge sources we want to drop completely).
// Backend enforces "must be truly empty" — 409s otherwise. The button
// itself is only rendered when customer.is_empty is true.
const deleteDialog = ref(false)
const deleteBusy = ref(false)

const submitDelete = async () => {
  if (!customer.value?.id) return
  deleteBusy.value = true
  try {
    await axios.delete(`/shipment/customers/${customer.value.id}/delete-if-empty`)
    showSnackbar({
      color: 'success',
      text: t('history.customers.delete_success') || 'Client supprimé',
    })
    deleteDialog.value = false
    // Go back to the customer list — this contact no longer exists.
    router.push('/history/customers')
  } catch (e) {
    console.error('Delete failed:', e)
    const data = e.response?.data
    let detail = data?.message || t('messages.something_went_wrong')
    if (data?.blocking) {
      const parts = []
      if (data.blocking.parcels > 0) parts.push(`${data.blocking.parcels} colis`)
      if (data.blocking.groups > 0) parts.push(`${data.blocking.groups} groupes`)
      if (data.blocking.invoices > 0) parts.push(`${data.blocking.invoices} factures`)
      if (data.blocking.chat_identities > 0) parts.push(`${data.blocking.chat_identities} identités chat`)
      if (data.blocking.merged_from > 0) parts.push(`${data.blocking.merged_from} contacts fusionnés vers ce client`)
      if (parts.length) detail = `${detail} (${parts.join(' · ')})`
    }
    showSnackbar({ color: 'error', text: detail })
  } finally {
    deleteBusy.value = false
  }
}

// ───────── Merge customer ─────────
// Manual de-dup: fold THIS customer (the page subject) into another
// canonical customer. Backend reassigns parcels, client_arrival_groups
// and shipping_transactions, then DMs the target's chatbot identity.
const mergeDialog = ref(false)
const mergeTarget = ref(null)
const mergeBusy = ref(false)
const targetCandidates = ref([])
const targetSearch = ref('')
const targetLoading = ref(false)
let targetSearchAbort = null

const openMergeDialog = () => {
  mergeTarget.value = null
  targetSearch.value = ''
  targetCandidates.value = []
  mergeDialog.value = true
}

const searchTargets = async query => {
  if (!query || query.length < 2) {
    targetCandidates.value = []
    return
  }
  // Cancel inflight to avoid race conditions on fast typing.
  if (targetSearchAbort) targetSearchAbort.abort()
  targetSearchAbort = new AbortController()
  targetLoading.value = true
  try {
    const res = await axios.get('/shipment/customers', {
      params: { search: query, per_page: 20 },
      signal: targetSearchAbort.signal,
    })
    const list = (res.data?.data || [])
      // Exclude the source from its own merge picker.
      .filter(c => c.id !== customer.value.id)
      .map(c => ({
        ...c,
        display_name: `${c.name || '?'}${c.phone ? ' · ' + c.phone : ''}`,
      }))
    targetCandidates.value = list
  } catch (e) {
    if (e.name !== 'CanceledError' && e.name !== 'AbortError') {
      console.error('Target customer search failed:', e)
    }
  } finally {
    targetLoading.value = false
  }
}

watch(targetSearch, val => {
  searchTargets(val)
})

const submitMerge = async () => {
  if (!mergeTarget.value?.id || !customer.value?.id) return
  mergeBusy.value = true
  try {
    const res = await axios.post(
      `/shipment/customers/${customer.value.id}/merge-into/${mergeTarget.value.id}`,
    )
    const d = res.data || {}
    const parts = []
    if (d.parcels_reassigned > 0) parts.push(`${d.parcels_reassigned} colis`)
    if (d.groups_merged > 0) parts.push(`${d.groups_merged} groupes fusionnés`)
    if (d.groups_rebranded > 0) parts.push(`${d.groups_rebranded} groupes transférés`)
    if (d.invoices_reassigned > 0) parts.push(`${d.invoices_reassigned} factures`)

    // DM status: Messenger/WhatsApp can refuse to send if we're outside
    // the 24h messaging window (Meta policy). Surface this so the
    // operator knows the merge succeeded but the customer didn't get
    // a notification — they'll see it next time they type /me.
    const dm = d.dm_status || {}
    let dmLine = ''
    if (dm.delivered > 0) {
      dmLine = ' · 📨 client notifié'
    } else if (dm.reason === 'outside_24h_window') {
      dmLine = ' · ⚠️ DM bloqué (fenêtre 24h Meta)'
    } else if (dm.reason === 'no_chat_identity') {
      dmLine = ' · ℹ️ client sans Messenger/WhatsApp'
    } else if (dm.reason && dm.reason !== 'not_attempted') {
      dmLine = ` · ⚠️ DM non envoyé (${dm.reason})`
    }

    showSnackbar({
      color: dm.reason === 'outside_24h_window' ? 'warning' : 'success',
      timeout: dm.reason === 'outside_24h_window' ? 8000 : 4000,
      text: parts.length
        ? `${t('history.customers.merge_success') || 'Fusion réussie'} (${parts.join(' · ')})${dmLine}`
        : `${t('history.customers.merge_success') || 'Fusion réussie'}${dmLine}`,
    })
    mergeDialog.value = false
    // Navigate to the target customer page — the source is now hidden.
    router.push(`/history/customers/${mergeTarget.value.id}`)
  } catch (e) {
    console.error('Merge failed:', e)
    showSnackbar({
      color: 'error',
      text: e.response?.data?.message || t('messages.something_went_wrong'),
    })
  } finally {
    mergeBusy.value = false
  }
}

// Lifecycle
onMounted(() => {
  fetchCustomer()
  fetchOrders()
})

// Refetch when the `:customerId` route param changes — same fix as on the
// parcel detail page. Without this, navigating from Cmd+K (or any in-page
// link) to a sibling customer keeps the page mounted with the previous
// customer's data; the URL updates but the content doesn't.
watch(customerId, (newId, oldId) => {
  if (newId && newId !== oldId) {
    fetchCustomer()
    fetchOrders()
  }
})
</script>
  
<style scoped>
.customer-details-main {
  font-family: 'Inter', 'Segoe UI', sans-serif;
}

.customer-details-header {
  border: 1px solid rgb(var(--v-theme-surface-variant));
  background: rgb(var(--v-theme-surface));
}

.customer-avatar {
  flex-shrink: 0;
}

.customer-header-actions {
  flex-wrap: wrap;
  gap: 8px;
}

.customer-info-card {
  border-left: 4px solid rgb(var(--v-theme-primary));
  border: 1px solid rgb(var(--v-theme-surface-variant));
}

.statistics-card {
  border-left: 4px solid rgb(var(--v-theme-secondary));
  border: 1px solid rgb(var(--v-theme-surface-variant));
}

.order-history-card {
  border-left: 4px solid rgb(var(--v-theme-success));
  border: 1px solid rgb(var(--v-theme-surface-variant));
}

.info-list .v-list-item {
  min-height: 40px;
  padding: 8px 0;
}

.statistics-grid {
  display: grid;
  gap: 12px;
}

.stat-item {
  padding: 12px;
  border-radius: 8px;
  border: 1px solid rgb(var(--v-theme-surface-variant));
  transition: all 0.2s ease-in-out;
}

.stat-item:hover {
  background: rgb(var(--v-theme-surface));
  border-color: rgb(var(--v-theme-primary));
}

.no-orders-section {
  opacity: 0.7;
}

.order-history-table {
  border-radius: 8px;
}

@media (max-width: 768px) {
  .customer-header-actions {
    flex-direction: column;
    align-items: stretch;
  }
  
  .customer-header-actions .v-btn {
    margin-bottom: 8px;
  }
}
</style>